// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/components/v2/history/MidasHistorySearchV2.tsx
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes
// Phase: 4 (sub-phase 4.3 — repalette to R5-0 designer JSX)
//
// Search input for the history page. Client-side only — feeds the parent's
// `searchQuery` state, which the page filters in-memory across loaded threads
// (locked product decision: no `?q=` server param exists).
//
// ⌘K / Ctrl+K focuses the input from anywhere on the page (desktop only,
// suppressed on phone where the keyboard hint isn't shown).
//
// Phase 4.3 (§3C, R5-0):
//   • bg #FFFFFF (was #F4EFE3 cream — read as elevated card not recessed field)
//   • border #E2DDD3 opaque warm-stone (was rgba(44,38,32,0.06) translucent ink)
//   • radius 10px (was rounded-lg / 8px)
//   • padding 12px / 14px from the JSX (replaces old h-14)
//   • placeholder copy "Search threads, machines, SR numbers…" (was "Search by
//     title or machine") — preserves SR-number search affordance
//   • search icon stroke #9A9186 strokeWidth 1.8 size 14×14
//   • placeholder text Sora 14/18 #9A9186
//   • ⌘ K hint: thin space (U+2009), bordered pill bg #FAF7F0 border #E2DDD3,
//     text #9A9186 JetBrains Mono 11/14 (was flush ⌘K, no border, flat ink bg)
import React, { useEffect, useRef } from "react";
import { Search, X } from "lucide-react";

interface MidasHistorySearchV2Props {
  value: string;
  onChange: (next: string) => void;
  /** Whether to show the "⌘ K" hint pill (desktop only). */
  showKeyboardHint: boolean;
  placeholder: string;
  ariaLabel: string;
  clearLabel: string;
}

const MidasHistorySearchV2: React.FC<MidasHistorySearchV2Props> = ({
  value,
  onChange,
  showKeyboardHint,
  placeholder,
  ariaLabel,
  clearLabel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // ⌘K / Ctrl+K → focus
  useEffect(() => {
    if (!showKeyboardHint) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showKeyboardHint]);

  return (
    <label
      htmlFor="midas-history-search"
      // R5-0 search container: rounded-[10px] py-3 px-3.5 gap-2.5 bg-white
      // border border-[#E2DDD3]. Focus-within keeps the existing terracotta
      // ring as accessibility polish — designer JSX is a static screenshot
      // and doesn't show focus state, so we layer it here.
      className="flex w-full items-center gap-2.5 rounded-[10px] border border-solid border-[#E2DDD3] bg-white px-3.5 py-3 transition-colors duration-150 focus-within:border-midas-terracotta focus-within:ring-2 focus-within:ring-[rgba(212,87,43,0.18)] motion-reduce:transition-none"
    >
      <Search
        className="flex-shrink-0 text-[#9A9186]"
        width={14}
        height={14}
        strokeWidth={1.8}
        aria-hidden="true"
      />
      <input
        id="midas-history-search"
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        aria-label={ariaLabel}
        // Sora 14/18 #9A9186 placeholder + body text. Disable browser default
        // outline ring; we use focus-within on the wrapper.
        // Sub-phase 4.6 §3G.4: `font-sora` Tailwind class binds Sora — drop
        // the redundant inline `style.fontFamily`.
        className="min-w-0 flex-1 bg-transparent font-sora text-[14px] leading-[18px] text-[#3D362F] placeholder:text-[#9A9186] focus:outline-none"
      />
      {value && (
        <button
          type="button"
          onClick={() => {
            onChange("");
            inputRef.current?.focus();
          }}
          aria-label={clearLabel}
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-[#9A9186] outline-none transition-colors duration-150 hover:bg-[#FAF7F0] hover:text-[#3D362F] focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
        </button>
      )}
      {showKeyboardHint && !value && (
        // ⌘ K hint per R5-0: rounded-sm py-0.75 px-1.5 (3px / 6px) bg-[#FAF7F0]
        // 1px border #E2DDD3, JBM 11/14 #9A9186, with thin space (U+2009)
        // between glyph and letter.
        <span
          aria-hidden="true"
          // Sub-phase 4.6 §3G.4: `font-mono` Tailwind class binds JetBrains
          // Mono — drop redundant inline fontFamily.
          className="flex-shrink-0 rounded-sm border border-solid border-[#E2DDD3] bg-[#FAF7F0] px-1.5 py-[3px] font-mono text-[11px] leading-[14px] text-[#9A9186]"
        >
          {"⌘ K"}
        </span>
      )}
    </label>
  );
};

export default MidasHistorySearchV2;
